import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getList,
} from "@surya-digital/leo-ts-types";
import { SurveyRuleConditionValidationError } from "./surveyRuleConditionValidationError";
import { SurveyRuleActionValidationError } from "./surveyRuleActionValidationError";


export namespace SurveyRuleValidationErrorGroupEnums {
  export namespace Target {
    export interface ConditionDTO {
      case: string;
      conditionId: string;
      validationErrors: SurveyRuleConditionValidationError.SurveyRuleConditionValidationErrorDTO[];
    }
    
    export class Condition {
      readonly isTargetCondition: boolean = true;
    
      readonly conditionId: LeoUUID;
      readonly validationErrors: SurveyRuleConditionValidationError.SurveyRuleConditionValidationError[];
    
    
      constructor(
      conditionId: LeoUUID, 
      validationErrors: SurveyRuleConditionValidationError.SurveyRuleConditionValidationError[]
      ) {
        this.conditionId = conditionId;
        this.validationErrors = validationErrors;
      }
    
      static fromDTO(dto: ConditionDTO): Condition {
        const conditionId = getLeoUuid(dto, "conditionId", InvalidSurveyRuleValidationErrorGroupTargetConditionConditionIdError);
        const validationErrors = getList<SurveyRuleConditionValidationError.SurveyRuleConditionValidationErrorDTO, SurveyRuleConditionValidationError.SurveyRuleConditionValidationError>(
          dto,
          "validationErrors",
          SurveyRuleConditionValidationError.fromDTO,
          InvalidSurveyRuleValidationErrorGroupTargetConditionValidationErrorsError
        );
        return new Condition(
          conditionId,
          validationErrors
        );
      }
    
      toDTO(): ConditionDTO {
        const dto: ConditionDTO = {
          case: "CONDITION",
            conditionId: this.conditionId.toDTO(),
            validationErrors: this.validationErrors.map((e: SurveyRuleConditionValidationError.SurveyRuleConditionValidationError) => { return SurveyRuleConditionValidationError.toDTO(e); })
        };
        return dto;
      }
      }
    
    Condition.prototype.toString = function toString(): string {
      return `Target.CONDITION(conditionId='${this.conditionId}',validationErrors='${this.validationErrors}')`;
    };
    
    export interface ActionDTO {
      case: string;
      validationErrors: SurveyRuleActionValidationError.SurveyRuleActionValidationErrorDTO[];
    }
    
    export class Action {
      readonly isTargetAction: boolean = true;
    
      readonly validationErrors: SurveyRuleActionValidationError.SurveyRuleActionValidationError[];
    
    
      constructor(
      validationErrors: SurveyRuleActionValidationError.SurveyRuleActionValidationError[]
      ) {
        this.validationErrors = validationErrors;
      }
    
      static fromDTO(dto: ActionDTO): Action {
        const validationErrors = getList<SurveyRuleActionValidationError.SurveyRuleActionValidationErrorDTO, SurveyRuleActionValidationError.SurveyRuleActionValidationError>(
          dto,
          "validationErrors",
          SurveyRuleActionValidationError.fromDTO,
          InvalidSurveyRuleValidationErrorGroupTargetConditionValidationErrorsError
        );
        return new Action(
          validationErrors
        );
      }
    
      toDTO(): ActionDTO {
        const dto: ActionDTO = {
          case: "ACTION",
            validationErrors: this.validationErrors.map((e: SurveyRuleActionValidationError.SurveyRuleActionValidationError) => { return SurveyRuleActionValidationError.toDTO(e); })
        };
        return dto;
      }
      }
    
    Action.prototype.toString = function toString(): string {
      return `Target.ACTION(validationErrors='${this.validationErrors}')`;
    };
    
  
    export type Target = Condition | Action;
  
    export type TargetDTO = ConditionDTO | ActionDTO;
  
    export const fromDTO = (dto: TargetDTO): Target => {
      switch (dto.case) {
        case "CONDITION":
            return Condition.fromDTO(dto as ConditionDTO);
        case "ACTION":
            return Action.fromDTO(dto as ActionDTO);
        default:
          throw new InvalidSurveyRuleValidationErrorGroupTargetError(
              `Case ${dto.case} is not valid case of Target.`
          );
      }
    };
  }
  
}
export interface SurveyRuleValidationErrorGroupDTO {
  ruleId: string;
  target: SurveyRuleValidationErrorGroupEnums.Target.TargetDTO;
}

export class SurveyRuleValidationErrorGroup {
  readonly isSurveyRuleValidationErrorGroupSurveyRuleValidationErrorGroup: boolean = true;

  readonly ruleId: LeoUUID;
  readonly target: SurveyRuleValidationErrorGroupEnums.Target.Target;


  constructor(
  ruleId: LeoUUID, 
  target: SurveyRuleValidationErrorGroupEnums.Target.Target
  ) {
    this.ruleId = ruleId;
    this.target = target;
  }

  static fromDTO(dto: SurveyRuleValidationErrorGroupDTO): SurveyRuleValidationErrorGroup {
    const ruleId = getLeoUuid(dto, "ruleId", InvalidSurveyRuleValidationErrorGroupRuleIdError);
    const target = SurveyRuleValidationErrorGroupEnums.Target.fromDTO(getObject(dto, "target", InvalidSurveyRuleValidationErrorGroupTargetError));
    return new SurveyRuleValidationErrorGroup(
      ruleId,
      target
    );
  }

  toDTO(): SurveyRuleValidationErrorGroupDTO {
    const dto: SurveyRuleValidationErrorGroupDTO = {
        ruleId: this.ruleId.toDTO(),
        target: this.target.toDTO()
    };
    return dto;
  }
  }

SurveyRuleValidationErrorGroup.prototype.toString = function toString(): string {
  return `SurveyRuleValidationErrorGroup(ruleId='${this.ruleId}',target='${this.target}')`;
};

export class InvalidSurveyRuleValidationErrorGroupError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleValidationErrorGroupError";
  }
}
export class InvalidSurveyRuleValidationErrorGroupRuleIdError extends InvalidSurveyRuleValidationErrorGroupError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleValidationErrorGroupRuleIdError";
  }
}

export class InvalidSurveyRuleValidationErrorGroupTargetError extends InvalidSurveyRuleValidationErrorGroupError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleValidationErrorGroupTargetError";
  }
}

export class InvalidSurveyRuleValidationErrorGroupTargetConditionError extends InvalidSurveyRuleValidationErrorGroupTargetError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleValidationErrorGroupTargetConditionError";
  }
}

export class InvalidSurveyRuleValidationErrorGroupTargetConditionConditionIdError extends InvalidSurveyRuleValidationErrorGroupTargetConditionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleValidationErrorGroupTargetConditionConditionIdError";
  }
}

export class InvalidSurveyRuleValidationErrorGroupTargetConditionValidationErrorsError extends InvalidSurveyRuleValidationErrorGroupTargetConditionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleValidationErrorGroupTargetConditionValidationErrorsError";
  }
}

export class InvalidSurveyRuleValidationErrorGroupTargetActionError extends InvalidSurveyRuleValidationErrorGroupTargetError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleValidationErrorGroupTargetActionError";
  }
}

export class InvalidSurveyRuleValidationErrorGroupTargetActionValidationErrorsError extends InvalidSurveyRuleValidationErrorGroupTargetActionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleValidationErrorGroupTargetActionValidationErrorsError";
  }
}
